/* One rule, opened from the Rules table: a panel at the right of the list, as the redesign draws it.
 *
 * **A panel over the list rather than a page of its own.** The list stays where it was, a reader can
 * open the next rule without going back, and the address still names the rule — `/rules/:ruleId` —
 * so a link from a notification, a citation or a colleague opens the same panel over the same list.
 *
 * **What the panel holds is the design's, in its order:** the rule's state and name with Edit and
 * Delete, the four-step journey from draft to active, the facts, a warning for `custom_sql`, and the
 * approval — which is the panel's real subject, because activation is the two-eyes step and the only
 * one that cannot be done alone. Two things are kept beyond the design because removing them would
 * remove a capability: publishing a new version of an active rule, and the version history that says
 * what each signature was for.
 *
 * **Every control is offered only to a role that holds it**, and the platform still refuses whatever
 * this panel offers by mistake. Where a step is waiting on a permission the reader lacks, the panel
 * says so in words rather than showing a control that would answer 403.
 */

import { useId, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ACTIVATE_RULE_PERMISSION,
  DELETE_RULE_PERMISSION,
  PUBLISH_RULE_PERMISSION,
  SUBMIT_RULE_PERMISSION,
  UPDATE_RULE_PERMISSION,
  can,
  useIdentity,
} from '@/entities/session';
import { ApiError } from '@/shared/api/http';
import { ageText } from '@/shared/model/age';
import { personName } from '@/shared/model/person';
import { Alert } from '@/shared/ui/Alert';
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog';
import { TextField } from '@/shared/ui/Field';
import { HelpLabel, InfoTip } from '@/shared/ui/InfoTip';
import { Modal } from '@/shared/ui/Modal';
import { SkeletonRows } from '@/shared/ui/Skeleton';
import { SidePanel } from '@/shared/ui/SidePanel';

import {
  bindingsBlockingDelete,
  useActivateRule,
  useDecideTransition,
  useDeleteRule,
  useRequestTransition,
  useRule,
  useRuleVersions,
  useTransitionRequest,
  useUpdateRule,
} from '../api/rules';
import type { Rule, RuleOverview, RuleVersion, TransitionRequest } from '../api/rules';
import { PHASE_TONE, isLiveRequest, phaseFrom } from '../model/overview';
import type { RulePhase } from '../model/overview';

import { PublishVersionForm } from './RuleForms';

/** The four steps the stepper draws. `awaiting` is "Two signatures" here, because on one rule's own
 * panel the step is the signatures rather than the waiting. */
const STEPS: readonly { readonly phase: RulePhase; readonly gate: boolean }[] = [
  { phase: 'draft', gate: false },
  { phase: 'published', gate: true },
  { phase: 'awaiting', gate: true },
  { phase: 'active', gate: false },
];

export function RuleDrawer({
  ruleId,
  row,
  onClose,
}: {
  readonly ruleId: string;
  /** The overview's row for this rule, when the page holds it: the severity and the live binding
   * count come from there. `undefined` for a rule past the overview's page, which still opens. */
  readonly row: RuleOverview | undefined;
  readonly onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const rule = useRule(ruleId);

  if (rule.isPending) {
    return (
      <SidePanel label={t('ruleDetail.loading')} onClose={onClose} head={null}>
        <SkeletonRows rows={5} columns={2} />
      </SidePanel>
    );
  }
  if (rule.isError) {
    return (
      <SidePanel label={t('ruleDetail.unreachable')} onClose={onClose} head={null}>
        <Alert tone="error" title={t('ruleDetail.unreachable')}>
          {rule.error.message}
        </Alert>
      </SidePanel>
    );
  }
  /* Keyed on the rule, so moving the panel to another rule starts its edit form, reason and
     confirmations afresh rather than carrying the last rule's into it. */
  return <RulePanel key={rule.data.id} rule={rule.data} row={row} onClose={onClose} />;
}

function RulePanel({
  rule,
  row,
  onClose,
}: {
  readonly rule: Rule;
  readonly row: RuleOverview | undefined;
  readonly onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const identity = useIdentity();
  const versions = useRuleVersions(rule.id);
  /* Read only for a draft, the one state a request governs. */
  const request = useTransitionRequest(rule.state === 'draft' ? rule.id : '');
  const deleteRule = useDeleteRule();
  const [editing, setEditing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const may = (permission: string): boolean =>
    identity.isSuccess && can(identity.data, permission);
  const latest = versions.data?.items[0] ?? null;
  const activation = rule.state === 'draft' ? (request.data ?? null) : null;
  const phase = phaseFrom(
    rule.state,
    latest !== null || (row?.latest_version ?? null) !== null,
    activation,
  );
  const blockingBindings = bindingsBlockingDelete(deleteRule.error);
  const severity = row?.severity ?? null;

  const head = (
    <>
      <span className={`lq-tag lq-tag--ru-${PHASE_TONE[phase]} lq-side__pill`}>
        {t(`rules.phase.${phase}.pill`)}
      </span>
      <h2 className="lq-side__name">{rule.name}</h2>
      {may(UPDATE_RULE_PERMISSION) || may(DELETE_RULE_PERMISSION) ? (
        <div className="lq-side__actions">
          {may(UPDATE_RULE_PERMISSION) ? (
            /* Disabled for a deprecated rule rather than hidden, with the reason on it: a control
               that vanishes leaves a reader unsure whether they lack the permission or the rule
               lacks the state. */
            <button
              type="button"
              className="lq-ru__small"
              disabled={rule.state === 'deprecated'}
              title={
                rule.state === 'deprecated'
                  ? t('ruleDetail.edit.refused.deprecated')
                  : t('ruleDetail.edit.meaning')
              }
              onClick={() => {
                setEditing(!editing);
              }}
            >
              {t('ruleDetail.edit.action')}
            </button>
          ) : null}
          {may(DELETE_RULE_PERMISSION) ? (
            <button
              type="button"
              className="lq-ru__small lq-ru__small--danger"
              title={t('ruleDetail.delete.meaning')}
              onClick={() => {
                setConfirmingDelete(true);
              }}
            >
              {t('ruleDetail.delete.action')}
            </button>
          ) : null}
        </div>
      ) : null}
    </>
  );

  return (
    <SidePanel label={rule.name} onClose={onClose} head={head}>
      <Stepper phase={phase} />

      {editing ? (
        <EditRuleForm
          rule={rule}
          onDone={() => {
            setEditing(false);
          }}
        />
      ) : null}

      <dl className="lq-ru__facts">
        <div className="lq-ru__fact">
          <dt>{t('ruleDrawer.fact.kind')}</dt>
          <dd className="lq-mono" title={t(`rules.kind.${rule.kind}`)}>
            {rule.kind}
          </dd>
        </div>
        <div className="lq-ru__fact">
          <dt>{t('ruleDrawer.fact.dimension')}</dt>
          <dd>
            {latest === null ? (
              <span className="lq-muted">—</span>
            ) : (
              t(`scoring.dimension.${latest.dimension}`)
            )}
          </dd>
        </div>
        <div className="lq-ru__fact">
          <dt>
            {t('ruleDrawer.fact.severity')}
            <InfoTip
              subject={t('ruleDrawer.fact.severity')}
              text={t('help.field.severity')}
            />
          </dt>
          <dd>
            {severity === null ? (
              /* A rule carries no severity of its own — its bindings do — so an unbound rule has
                 none to show rather than a default. */
              <span className="lq-muted" title={t('ruleDrawer.noSeverity')}>
                —
              </span>
            ) : (
              t(`rules.severity.${severity}.label`)
            )}
          </dd>
        </div>
        <div className="lq-ru__fact">
          <dt>{t('ruleDrawer.fact.version')}</dt>
          <dd>
            {latest === null ? (
              <span className="lq-muted">{t('ruleDrawer.noVersion')}</span>
            ) : (
              <span className="lq-mono">v{latest.version}</span>
            )}
          </dd>
        </div>
        <div className="lq-ru__fact">
          <dt>{t('ruleDrawer.fact.owner')}</dt>
          <dd title={rule.owner}>{personName(rule.owner)}</dd>
        </div>
        <div className="lq-ru__fact">
          <dt>
            {t('ruleDrawer.fact.bindings')}
            <InfoTip
              subject={t('ruleDrawer.fact.bindings')}
              text={t('help.field.boundTo')}
            />
          </dt>
          <dd>{row === undefined ? '—' : String(row.live_bindings)}</dd>
        </div>
        {rule.description === null ? null : (
          <div className="lq-ru__fact">
            <dt>{t('ruleDrawer.fact.description')}</dt>
            <dd>{rule.description}</dd>
          </div>
        )}
        {rule.tags.length === 0 ? null : (
          <div className="lq-ru__fact">
            <dt>{t('ruleDrawer.fact.tags')}</dt>
            <dd>{rule.tags.join(', ')}</dd>
          </div>
        )}
      </dl>

      {rule.kind === 'custom_sql' ? (
        <p className="lq-tintband lq-tintband--warn">
          <span className="lq-tintband__text">
            <strong className="lq-mono">custom_sql</strong> {t('ruleDrawer.customSql')}
          </span>
        </p>
      ) : null}

      <ApprovalPanel
        rule={rule}
        latest={latest}
        versionsRead={versions.isSuccess}
        request={request}
        may={may}
        subject={identity.data?.subject ?? null}
      />

      <VersionsPanel versions={versions} />

      <ConfirmDialog
        open={confirmingDelete}
        title={t('ruleDetail.delete.confirmTitle')}
        message={
          blockingBindings === null
            ? t('ruleDetail.delete.confirmBody', { name: rule.name })
            : t('ruleDetail.delete.confirmBodyWithBindings', {
                name: rule.name,
                bindings: blockingBindings,
              })
        }
        confirmLabel={
          blockingBindings === null
            ? t('ruleDetail.delete.action')
            : t('ruleDetail.delete.actionWithBindings', { bindings: blockingBindings })
        }
        cancelLabel={t('common.cancel')}
        tone="danger"
        busy={deleteRule.isPending}
        /* The refusal that *offers* the cascade is not an error: it is the question re-asked with
           the number the server reported. Every other refusal shows. */
        error={
          deleteRule.isError && blockingBindings === null
            ? deleteRule.error.message
            : undefined
        }
        onConfirm={() => {
          deleteRule.mutate(
            blockingBindings === null
              ? { ruleId: rule.id }
              : { ruleId: rule.id, retireBindings: blockingBindings },
            {
              onSuccess: () => {
                setConfirmingDelete(false);
                /* The panel reads a rule that no longer exists; closing it leaves the list, where
                   the row is simply gone. */
                onClose();
              },
            },
          );
        }}
        onCancel={() => {
          setConfirmingDelete(false);
          deleteRule.reset();
        }}
      />
    </SidePanel>
  );
}

/** Draft → Published → Two signatures → Active, with the gates dashed. A deprecated rule has left
 * the journey, so every step is grey rather than one of them claiming to be where it is. */
function Stepper({ phase }: { readonly phase: RulePhase }): ReactElement {
  const { t } = useTranslation();
  const at = STEPS.findIndex((step) => step.phase === phase);

  return (
    <ol className="lq-ru__steps" aria-label={t('rules.lifecycle.label')}>
      {STEPS.map((step, index) => {
        const reached = phase !== 'deprecated' && index < at;
        const here = phase !== 'deprecated' && index === at;
        return (
          <li
            key={step.phase}
            className="lq-ru__stepItem"
            aria-current={here ? 'step' : undefined}
          >
            <span className="lq-ru__stepMark">
              <span
                className={
                  here
                    ? 'lq-ru__dot lq-ru__dot--here'
                    : reached
                      ? 'lq-ru__dot lq-ru__dot--done'
                      : 'lq-ru__dot'
                }
                aria-hidden="true"
              />
              <span
                className={
                  here ? 'lq-ru__stepName lq-ru__stepName--here' : 'lq-ru__stepName'
                }
              >
                {t(`ruleDrawer.step.${step.phase}`)}
              </span>
            </span>
            {index < STEPS.length - 1 ? (
              <span
                className={
                  STEPS[index + 1]?.gate === true
                    ? 'lq-ru__stepLine lq-ru__stepLine--gate'
                    : 'lq-ru__stepLine'
                }
                aria-hidden="true"
              />
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}

/** Editing a rule's metadata: name, owner, description and tags — never what it measures.
 *
 * **Nothing here can change what the rule measures, and the type is the reason.** `RuleEdit` has no
 * expression field and the route refuses unknown keys, so a version cannot be reached from this form
 * however it is edited later. Changing the expression is publishing a version. Seeded from the rule,
 * which is what makes it an edit rather than a re-entry.
 */
function EditRuleForm({
  rule,
  onDone,
}: {
  readonly rule: Rule;
  readonly onDone: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const update = useUpdateRule();
  const descriptionId = useId();
  const [name, setName] = useState(rule.name);
  const [owner, setOwner] = useState(rule.owner);
  const [description, setDescription] = useState(rule.description ?? '');
  const [tagsStr, setTagsStr] = useState(rule.tags.join(', '));

  const trimmedName = name.trim();
  const trimmedOwner = owner.trim();
  /* Refused here rather than sent: the server refuses it too, and a reader answers it far better
     beside the field than as a banner. */
  const incomplete = trimmedName === '' || trimmedOwner === '';

  return (
    <form
      className="lq-ru__edit"
      onSubmit={(event) => {
        event.preventDefault();
        if (incomplete) return;
        update.mutate(
          {
            ruleId: rule.id,
            name: trimmedName,
            owner: trimmedOwner,
            /* Empty means cleared, never omitted: the route is a full replacement. */
            description: description.trim() === '' ? null : description.trim(),
            tags: tagsStr
              .split(',')
              .map((tag) => tag.trim())
              .filter(Boolean),
          },
          { onSuccess: onDone },
        );
      }}
    >
      <div className="lq-ru__editTitle">{t('ruleDetail.edit.title')}</div>
      <TextField label={t('rules.field.name')} value={name} onChange={setName} required />
      <TextField
        label={t('rules.field.owner')}
        value={owner}
        onChange={setOwner}
        required
      />
      <div className="lq-ru__textareaField">
        <label htmlFor={descriptionId}>{t('ruleDetail.edit.descriptionLabel')}</label>
        <textarea
          id={descriptionId}
          className="lq-ru__textarea"
          rows={2}
          value={description}
          onChange={(event) => {
            setDescription(event.target.value);
          }}
        />
      </div>
      <TextField
        label={t('ruleDetail.edit.tagsLabel')}
        value={tagsStr}
        onChange={setTagsStr}
        placeholder={t('ruleDetail.edit.tagsPlaceholder')}
      />
      <p className="lq-ru__note">{t('ruleDrawer.edit.fixed')}</p>
      {update.isError ? (
        /* A 409 is the expected answer for two ordinary mistakes — a name already taken, or a
           deprecated rule — so it is a notice; anything else keeps the error tone. */
        <Alert
          tone={
            update.error instanceof ApiError && update.error.status === 409
              ? 'notice'
              : 'error'
          }
          title={t('ruleDetail.edit.refusedTitle')}
        >
          {update.error.message}
        </Alert>
      ) : null}
      <div className="lq-ru__editActions">
        <button type="button" className="lq-ru__small" onClick={onDone}>
          {t('ruleDetail.edit.cancel')}
        </button>
        <button
          type="submit"
          className="lq-ru__small lq-ru__small--primary"
          disabled={incomplete || update.isPending}
          title={incomplete ? t('ruleDetail.edit.incomplete') : undefined}
        >
          {t('ruleDetail.edit.submit')}
        </button>
      </div>
    </form>
  );
}

/** The two-eyes step, in whichever of its five states this rule is in.
 *
 * **Who may sign is decided by the server, and shown here only so a click is not wasted.** The
 * requester's own signature is refused by identity whatever role they hold, so a requester is told so
 * in words rather than shown buttons that would answer 403. A reason is required on both decisions,
 * because it is what an auditor reads a year later.
 */
function ApprovalPanel({
  rule,
  latest,
  versionsRead,
  request,
  may,
  subject,
}: {
  readonly rule: Rule;
  readonly latest: RuleVersion | null;
  readonly versionsRead: boolean;
  readonly request: ReturnType<typeof useTransitionRequest>;
  readonly may: (permission: string) => boolean;
  readonly subject: string | null;
}): ReactElement {
  const { t } = useTranslation();
  const ask = useRequestTransition();
  const decide = useDecideTransition();
  const activate = useActivateRule();
  const [reason, setReason] = useState('');
  const [publishing, setPublishing] = useState(false);
  const reasonId = useId();
  const [now] = useState(() => new Date());

  const publishButton = (label: string, primary = false): ReactElement | null =>
    may(PUBLISH_RULE_PERMISSION) ? (
      <button
        type="button"
        className={primary ? 'lq-ru__act lq-ru__act--primary' : 'lq-ru__act'}
        onClick={() => {
          setPublishing(true);
        }}
      >
        {label}
      </button>
    ) : null;

  let body: ReactNode;
  if (rule.state === 'deprecated') {
    body = <p className="lq-ru__line">{t('ruleDrawer.approval.deprecated')}</p>;
  } else if (rule.state === 'active') {
    body = (
      <>
        <p className="lq-ru__line">{t('ruleDrawer.approval.active')}</p>
        {publishButton(t('ruleDrawer.approval.publishNext'))}
      </>
    );
  } else if (request.isPending || !versionsRead) {
    body = <p className="lq-ru__line">{t('approval.loading')}</p>;
  } else if (request.isError) {
    /* A failed read is about the request, never "nobody has asked" — that would invite a second
       request the kernel would refuse. */
    body = (
      <Alert tone="warning" title={t('approval.unreadableTitle')}>
        {request.error.message}
      </Alert>
    );
  } else if (request.data === null || !isLiveRequest(request.data)) {
    const closed = request.data;
    body =
      latest === null ? (
        <>
          <p className="lq-ru__line">{t('ruleDrawer.approval.needsPublish')}</p>
          {publishButton(t('ruleDrawer.approval.publishFirst'))}
          {may(PUBLISH_RULE_PERMISSION) ? null : (
            <p className="lq-ru__note">{t('ruleDrawer.approval.publishNotYours')}</p>
          )}
        </>
      ) : (
        <>
          <p className="lq-ru__line">{t('ruleDrawer.approval.canRequest')}</p>
          {closed?.state === 'rejected' ? (
            <p className="lq-ru__note">{t('ruleDrawer.approval.lastRejected')}</p>
          ) : null}
          {may(SUBMIT_RULE_PERMISSION) ? null : (
            <p className="lq-ru__note">{t('ruleDrawer.approval.requestNotYours')}</p>
          )}
          {ask.isError ? <Alert tone="error">{ask.error.message}</Alert> : null}
          <div className="lq-ru__actRow">
            <button
              type="button"
              className="lq-ru__act lq-ru__act--primary"
              disabled={!may(SUBMIT_RULE_PERMISSION) || ask.isPending}
              onClick={() => {
                ask.mutate(rule.id);
              }}
            >
              {t('ruleDrawer.approval.request')}
            </button>
            {publishButton(t('ruleDrawer.approval.publishNext'))}
          </div>
        </>
      );
  } else {
    const current = request.data;
    const signatures = <Signatures request={current} now={now} />;
    if (current.satisfied) {
      body = (
        <>
          <p className="lq-ru__satisfied">
            {t('ruleDrawer.approval.satisfied', {
              collected: current.eyes_collected,
              required: current.required_eyes,
            })}
          </p>
          {signatures}
          {activate.isError ? (
            <Alert tone="notice" title={t('ruleDetail.activate.refusedTitle')}>
              {activate.error.message}
            </Alert>
          ) : null}
          {may(ACTIVATE_RULE_PERMISSION) ? (
            <button
              type="button"
              className="lq-ru__act lq-ru__act--primary"
              disabled={activate.isPending}
              onClick={() => {
                activate.mutate(rule.id);
              }}
            >
              {t('rules.activate.action')}
            </button>
          ) : (
            <p className="lq-ru__note">{t('ruleDrawer.approval.activateNotYours')}</p>
          )}
        </>
      );
    } else {
      const mine = subject !== null && subject === current.requester;
      const blocked = !may(ACTIVATE_RULE_PERMISSION);
      body = (
        <>
          <div className="lq-ru__count">
            <span className="lq-ru__countFigure">
              {t('approval.eyes', {
                collected: current.eyes_collected,
                required: current.required_eyes,
              })}
            </span>
            <span className="lq-ru__countWho" title={current.requester}>
              {mine
                ? t('ruleDrawer.approval.requestedByYou', {
                    who: personName(current.requester),
                  })
                : t('ruleDrawer.approval.requestedBy', {
                    who: personName(current.requester),
                  })}
            </span>
          </div>
          {signatures}
          {mine ? (
            /* The comparison the server makes, made here so the click is not wasted. */
            <p className="lq-ru__note">{t('approval.ownRequestBody')}</p>
          ) : (
            <>
              <div className="lq-ru__textareaField">
                <label htmlFor={reasonId}>{t('ruleDrawer.approval.reasonLabel')}</label>
                <textarea
                  id={reasonId}
                  className="lq-ru__textarea"
                  rows={2}
                  maxLength={500}
                  value={reason}
                  placeholder={t('ruleDrawer.approval.reasonPlaceholder')}
                  onChange={(event) => {
                    setReason(event.target.value);
                  }}
                />
              </div>
              {decide.isError ? (
                <p className="lq-tintband lq-tintband--bad" role="alert">
                  <span className="lq-tintband__text">
                    <strong>{t('ruleDrawer.approval.refused')}</strong>{' '}
                    {decide.error.message}
                  </span>
                </p>
              ) : null}
              {blocked ? (
                <p className="lq-ru__note">{t('ruleDrawer.approval.signNotYours')}</p>
              ) : null}
              {/* Reject first in the DOM: the consequential control is not the one under a cursor
                  that arrived by habit. */}
              <div className="lq-ru__actRow">
                <button
                  type="button"
                  className="lq-ru__act"
                  disabled={blocked || reason.trim() === '' || decide.isPending}
                  onClick={() => {
                    decide.mutate(
                      { ruleId: rule.id, approved: false, reason },
                      {
                        onSuccess: () => {
                          setReason('');
                        },
                      },
                    );
                  }}
                >
                  {t('approval.reject')}
                </button>
                <button
                  type="button"
                  className="lq-ru__act lq-ru__act--primary"
                  disabled={blocked || reason.trim() === '' || decide.isPending}
                  title={t('approval.approveMeaning')}
                  onClick={() => {
                    decide.mutate(
                      { ruleId: rule.id, approved: true, reason },
                      {
                        onSuccess: () => {
                          setReason('');
                        },
                      },
                    );
                  }}
                >
                  {t('approval.approve')}
                </button>
              </div>
            </>
          )}
        </>
      );
    }
  }

  return (
    <section className="lq-ru__card" aria-labelledby={`${reasonId}-title`}>
      <HelpLabel
        subject={t('ruleDrawer.approval.title')}
        help={t('help.term.ruleApproval')}
      >
        <h3 id={`${reasonId}-title`} className="lq-ru__cardTitle">
          {t('ruleDrawer.approval.title')}
        </h3>
      </HelpLabel>
      {body}
      {/* Publishing in a dialog, because the form is a revision of a frozen expression and wants the
          room. Mounted only while open, so a second opening starts from the latest version. */}
      <Modal
        open={publishing}
        title={t('rules.version.publish.title')}
        onClose={() => {
          setPublishing(false);
        }}
      >
        <PublishVersionForm
          rule={rule}
          bare
          onPublished={() => {
            setPublishing(false);
          }}
        />
      </Modal>
    </section>
  );
}

function Signatures({
  request,
  now,
}: {
  readonly request: TransitionRequest;
  readonly now: Date;
}): ReactElement | null {
  const { t } = useTranslation();
  if (request.decisions.length === 0) return null;
  return (
    <ul className="lq-ru__sigs">
      {request.decisions.map((decision) => (
        <li key={`${decision.approver}-${decision.decided_at}`} className="lq-ru__sig">
          <div className="lq-ru__sigHead">
            <strong title={decision.approver}>{personName(decision.approver)}</strong>
            <span
              className={
                decision.kind === 'approve'
                  ? 'lq-ru__verdict lq-ru__verdict--ok'
                  : 'lq-ru__verdict lq-ru__verdict--bad'
              }
            >
              {t(`approval.decision.${decision.kind}`)}
            </span>
            <span className="lq-ru__sigWhen" title={decision.decided_at}>
              {ageText(t, decision.decided_at, now) ?? decision.decided_at}
            </span>
          </div>
          {/* The reason, verbatim. It is what an auditor reads a year later. */}
          <div className="lq-ru__sigReason">“{decision.reason}”</div>
        </li>
      ))}
    </ul>
  );
}

/** Every published version, newest first — what each signature was for, and what ran when. */
function VersionsPanel({
  versions,
}: {
  readonly versions: ReturnType<typeof useRuleVersions>;
}): ReactElement {
  const { t } = useTranslation();
  const [now] = useState(() => new Date());

  return (
    <section className="lq-ru__card" aria-labelledby="ru-versions">
      <HelpLabel
        subject={t('ruleDetail.versions.title')}
        help={t('help.term.ruleVersion')}
      >
        <h3 id="ru-versions" className="lq-ru__cardTitle">
          {t('ruleDetail.versions.title')}
        </h3>
      </HelpLabel>
      {versions.isPending ? <SkeletonRows rows={2} /> : null}
      {versions.isError ? <Alert tone="error">{versions.error.message}</Alert> : null}
      {versions.isSuccess && versions.data.items.length === 0 ? (
        <p className="lq-ru__line">{t('ruleDetail.versions.empty')}</p>
      ) : null}
      {versions.isSuccess && versions.data.items.length > 0 ? (
        <ul className="lq-ru__versions">
          {versions.data.items.map((version) => (
            <li key={version.id} className="lq-ru__version">
              <span className="lq-mono lq-ru__versionLabel">v{version.version}</span>
              <span className="lq-ru__versionCols lq-mono">
                {version.expression.columns.length === 0
                  ? t('ruleDetail.versions.wholeDataset')
                  : version.expression.columns.join(', ')}
              </span>
              <span className="lq-ru__versionWhen" title={version.published_at}>
                {t('ruleDrawer.versionBy', {
                  who: personName(version.published_by),
                  age: ageText(t, version.published_at, now) ?? version.published_at,
                })}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
